import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getDeliveries,
  createDelivery,
  patchDelivery,
  createMission,
  getDrones,
} from '../api'
import DeliveryMapPanel from '../components/DeliveryMapPanel'
import './Deliveries.css'

function parseLocationJson(
  raw: string | undefined
): { latitude: number; longitude: number } | null {
  if (!raw || typeof raw !== 'string') return null
  try {
    const o = JSON.parse(raw) as { latitude?: number; longitude?: number }
    if (typeof o.latitude === 'number' && typeof o.longitude === 'number') {
      return { latitude: o.latitude, longitude: o.longitude }
    }
  } catch {
    /* ignore */
  }
  return null
}

export default function Deliveries() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [deliveryNumber, setDeliveryNumber] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [recipientPhone, setRecipientPhone] = useState('')
  const [latA, setLatA] = useState('32.085300')
  const [lngA, setLngA] = useState('34.781800')
  const [latB, setLatB] = useState('32.090000')
  const [lngB, setLngB] = useState('34.790000')
  const [speed, setSpeed] = useState(8)
  const [pickMode, setPickMode] = useState<'A' | 'B' | null>(null)

  const [assignDeliveryId, setAssignDeliveryId] = useState('')
  const [assignDroneId, setAssignDroneId] = useState('')

  const { data: deliveries = [], isLoading, error } = useQuery({
    queryKey: ['deliveries'],
    queryFn: getDeliveries,
  })

  const { data: drones = [] } = useQuery({
    queryKey: ['drones'],
    queryFn: getDrones,
  })

  const createMutation = useMutation({
    mutationFn: createDelivery,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] })
      setShowAdd(false)
      setDeliveryNumber('')
      setRecipientName('')
      setRecipientPhone('')
    },
  })

  const assignMutation = useMutation({
    mutationFn: async ({
      deliveryId,
      droneId,
    }: {
      deliveryId: string
      droneId: string
    }) => {
      const list = queryClient.getQueryData([
        'deliveries',
      ]) as Array<{
        id: string
        delivery_number: string
        pickup_location?: string
        delivery_location?: string
      }> | undefined
      const d = list?.find((x) => x.id === deliveryId)
      if (!d) throw new Error('משלוח לא נמצא')
      const pickup = parseLocationJson(d.pickup_location)
      const drop = parseLocationJson(d.delivery_location)
      if (!pickup || !drop) throw new Error('מיקומי איסוף/מסירה לא תקינים')
      const mission = await createMission({
        drone_id: droneId,
        name: `משלוח ${d.delivery_number}`,
        type: 'delivery',
        waypoints: [
          { latitude: pickup.latitude, longitude: pickup.longitude },
          { latitude: drop.latitude, longitude: drop.longitude },
        ],
      })
      await patchDelivery(deliveryId, {
        drone_id: droneId,
        mission_id: mission.id,
        status: 'assigned',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliveries'] })
      queryClient.invalidateQueries({ queryKey: ['missions'] })
      setAssignDeliveryId('')
      setAssignDroneId('')
    },
  })

  const handleLatLngChange = useCallback((point: 'A' | 'B', lat: number, lng: number) => {
    if (point === 'A') {
      setLatA(lat.toFixed(6))
      setLngA(lng.toFixed(6))
    } else {
      setLatB(lat.toFixed(6))
      setLngB(lng.toFixed(6))
    }
  }, [])

  const resetMapCoords = useCallback(() => {
    setLatA('')
    setLngA('')
    setLatB('')
    setLngB('')
    setPickMode(null)
  }, [])

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault()
    if (!deliveryNumber.trim()) return
    const pa = parseFloat(latA)
    const pna = parseFloat(lngA)
    const pb = parseFloat(latB)
    const pnb = parseFloat(lngB)
    if ([pa, pna, pb, pnb].some((n) => Number.isNaN(n))) return
    createMutation.mutate({
      delivery_number: deliveryNumber.trim(),
      recipient_name: recipientName.trim() || undefined,
      recipient_phone: recipientPhone.trim() || undefined,
      pickup_location: { latitude: pa, longitude: pna },
      delivery_location: { latitude: pb, longitude: pnb },
      speed_mps: speed,
    })
  }

  if (isLoading) return <div className="page-loading">טוען משלוחים...</div>
  if (error) return <div className="page-error">שגיאה בטעינת משלוחים</div>

  return (
    <div className="deliveries-page">
      <div className="deliveries-header">
        <h1>{t('deliveries.title')}</h1>
        <button type="button" className="btn-primary" onClick={() => setShowAdd(true)}>
          + משלוח חדש
        </button>
      </div>

      {showAdd && (
        <div className="deliveries-form-card deliveries-with-map">
          <h3>משלוח חדש — מפה (מבוסס על המערכת הקודמת)</h3>
          <p className="deliveries-lead">
            בחר נקודת התחלה (איסוף) וסיום (מסירה) על המפה או הזן קואורדינטות. מרחק ו־ETA נשמרים בשרת
            (אותו חישוב haversine כמו ב־<code>assets/js/delivery.js</code>).
          </p>
          <form onSubmit={handleAdd} className="deliveries-form-row">
            <div className="deliveries-fields">
              <label>
                מספר משלוח (חובה)
                <input
                  value={deliveryNumber}
                  onChange={(e) => setDeliveryNumber(e.target.value)}
                  placeholder="DEL-001"
                  required
                />
              </label>
              <label>
                שם נמען
                <input
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="אופציונלי"
                />
              </label>
              <label>
                טלפון נמען
                <input
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="אופציונלי"
                />
              </label>
              <div className="coords-toolbar">
                <div className="coord-row">
                  <span className="coord-label">A (איסוף):</span>
                  <input
                    type="number"
                    step="any"
                    value={latA}
                    onChange={(e) => setLatA(e.target.value)}
                    placeholder="רוחב"
                  />
                  <input
                    type="number"
                    step="any"
                    value={lngA}
                    onChange={(e) => setLngA(e.target.value)}
                    placeholder="אורך"
                  />
                  <button
                    type="button"
                    className="btn-map-pick"
                    onClick={() => setPickMode('A')}
                  >
                    בחר A מהמפה
                  </button>
                </div>
                <div className="coord-row">
                  <span className="coord-label">B (מסירה):</span>
                  <input
                    type="number"
                    step="any"
                    value={latB}
                    onChange={(e) => setLatB(e.target.value)}
                    placeholder="רוחב"
                  />
                  <input
                    type="number"
                    step="any"
                    value={lngB}
                    onChange={(e) => setLngB(e.target.value)}
                    placeholder="אורך"
                  />
                  <button
                    type="button"
                    className="btn-map-pick"
                    onClick={() => setPickMode('B')}
                  >
                    בחר B מהמפה
                  </button>
                </div>
                <div className="coord-row">
                  <span className="coord-label">מהירות (מ׳/ש׳):</span>
                  <input
                    type="number"
                    step="0.1"
                    min={0.5}
                    value={speed}
                    onChange={(e) => setSpeed(parseFloat(e.target.value) || 8)}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowAdd(false)}>
                  ביטול
                </button>
                <button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'שומר...' : 'שמור משלוח'}
                </button>
              </div>
            </div>
          </form>

          <DeliveryMapPanel
            latA={latA}
            lngA={lngA}
            latB={latB}
            lngB={lngB}
            speed={speed}
            onLatLngChange={handleLatLngChange}
            pickMode={pickMode}
            onPickModeChange={setPickMode}
            onResetAll={resetMapCoords}
          />
        </div>
      )}

      <div className="deliveries-assign panel-assign">
        <h3>שיוך רחפן ומשימת משלוח</h3>
        <p className="assign-help">
          יוצר משימת <code>delivery</code> עם שתי נקודות (איסוף → מסירה), משייך לרחפן ומעדכן סטטוס ל־
          <code>assigned</code>.
        </p>
        <div className="assign-row">
          <label>
            משלוח
            <select
              value={assignDeliveryId}
              onChange={(e) => setAssignDeliveryId(e.target.value)}
            >
              <option value="">בחר משלוח</option>
              {deliveries.map((d: { id: string; delivery_number: string }) => (
                <option key={d.id} value={d.id}>
                  {d.delivery_number}
                </option>
              ))}
            </select>
          </label>
          <label>
            רחפן
            <select value={assignDroneId} onChange={(e) => setAssignDroneId(e.target.value)}>
              <option value="">בחר רחפן</option>
              {drones.map((d: { id: string; drone_id: string }) => (
                <option key={d.id} value={d.id}>
                  {d.drone_id}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn-primary"
            disabled={!assignDeliveryId || !assignDroneId || assignMutation.isPending}
            onClick={() =>
              assignMutation.mutate({
                deliveryId: assignDeliveryId,
                droneId: assignDroneId,
              })
            }
          >
            {assignMutation.isPending ? 'משייך...' : 'צור משימה ושייך'}
          </button>
        </div>
        {assignMutation.isError && (
          <p className="assign-error">{(assignMutation.error as Error).message}</p>
        )}
      </div>

      {deliveries.length === 0 && !showAdd ? (
        <p className="page-empty">אין משלוחים. צור משלוח חדש באמצעות הכפתור למעלה.</p>
      ) : (
        <div className="deliveries-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>מס׳ משלוח</th>
                <th>סטטוס</th>
                <th>נמען</th>
                <th>מרחק (מ׳)</th>
                <th>ETA (ש׳)</th>
                <th>מיקום מסירה</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map(
                (d: {
                  id: string
                  delivery_number: string
                  status: string
                  recipient_name?: string
                  delivery_location?: string
                  distance_meters?: number | null
                  estimated_time_seconds?: number | null
                }) => (
                  <tr key={d.id}>
                    <td>{d.delivery_number}</td>
                    <td>
                      <span className={`status status-${d.status}`}>{d.status}</span>
                    </td>
                    <td>{d.recipient_name || '—'}</td>
                    <td>
                      {d.distance_meters != null ? Math.round(d.distance_meters) : '—'}
                    </td>
                    <td>{d.estimated_time_seconds ?? '—'}</td>
                    <td>
                      {typeof d.delivery_location === 'string' ? d.delivery_location : '—'}
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
